import { useEffect, useState } from 'react';
import { companionApi } from '../utils/companionApi.js';
import { useT } from '../utils/i18n';
import { formatDateTime } from '../utils/dates.js';

function Ring({ owned, total, label, unknown }) {
  const pct = total > 0 ? Math.min(100, Math.round((owned / total) * 100)) : 0;
  const r = 26, c = 2 * Math.PI * r;
  return (
    <div className="cmp-ring">
      <svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true">
        <circle cx="32" cy="32" r={r} className="cmp-ring-track" />
        {!unknown && <circle cx="32" cy="32" r={r} className="cmp-ring-fill" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />}
      </svg>
      <div className="cmp-ring-num">{unknown ? '—' : `${owned}/${total}`}</div>
      <div className="cmp-ring-label">{label}</div>
    </div>
  );
}

// Honest completion for one set: base numbers, every number (secrets included)
// and catalog-supported finish slots. Three different questions, three rings.
export default function CompletionRings({ setId, data: given, setName, onShowMissing }) {
  const { t } = useT();
  const [data, setData] = useState(given || null);
  const [error, setError] = useState(null);
  const [showMissing, setShowMissing] = useState(false);

  useEffect(() => {
    if (given || !setId) return;
    let live = true;
    companionApi.completion(setId).then(d => { if (live) setData(d); }).catch(e => { if (live) setError(e); });
    return () => { live = false; };
  }, [setId, given]);

  if (error) return <div className="cmp-note cmp-note-error">{t('companion.completionUnavailable')}</div>;
  if (!data) return <div className="cmp-note">{t('common.loading')}</div>;
  const base = data.base || {}, all = data.all_numbers || {}, master = data.master || {};
  // Secret numbers (above the printed total) arrive from the grounded set_progress tool; older results have none.
  const secret = data.secret && typeof data.secret === 'object' ? data.secret : null;
  const missing = base.missing || [];
  return (
    <div className="cmp-completion">
      {setName && <div className="cmp-card-title">{setName}</div>}
      <div className="cmp-rings">
        <Ring owned={base.owned ?? 0} total={base.total ?? 0} label={t('companion.completion.base')} />
        {secret && secret.total != null && <Ring owned={secret.owned ?? 0} total={secret.total ?? 0} label={t('companion.completion.secret')} />}
        <Ring owned={all.owned ?? 0} total={all.total ?? 0} label={t('companion.completion.allNumbers')} />
        <Ring owned={master.owned ?? 0} total={master.total ?? 0} label={t('companion.completion.master')} unknown={master.known === false} />
      </div>
      {master.known === false && <div className="cmp-note">{t('companion.completion.masterUnknown')}</div>}
      {data.catalog_complete === false && <div className="cmp-note">{t('companion.completion.catalogIncomplete')}</div>}
      {missing.length > 0 && (
        <div>
          <button type="button" className="cmp-link" onClick={() => setShowMissing(s => !s)}>
            {showMissing ? t('companion.completion.hideMissing') : t('companion.completion.showMissing', { count: missing.length })}
          </button>
          {showMissing && (
            <div className="cmp-missing">
              {missing.map(n => <span key={String(n)} className="cmp-missing-num">{typeof n === 'object' ? (n.number ?? n.id) : n}</span>)}
            </div>
          )}
          {onShowMissing && <button type="button" className="cmp-link" onClick={() => onShowMissing(data)}>{t('companion.completion.openInCollection')}</button>}
        </div>
      )}
      {formatDateTime(data.as_of) && <div className="cmp-asof">{t('companion.asOf', { date: formatDateTime(data.as_of) })}</div>}
    </div>
  );
}
